interface UserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  userId?: string;
  firebaseUid?: string;
  keycloak_user_id?: string;
  cityId?: string;
  city?: unknown;
  state?: string;
  apiToken?: string;
  tokenData?: {
    accessToken?: string;
  };
  picture?: string;
  [key: string]: unknown;
}

function toPersistedUserData(userData: UserData): UserData {
  return {
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    userId: userData.userId,
    firebaseUid: userData.firebaseUid,
    keycloak_user_id: userData.keycloak_user_id,
    cityId: userData.cityId,
    city: userData.city,
    state: userData.state,
    picture: userData.picture,
  };
}

class UserStore {
  private user: UserData | null = null;
  private listeners: Array<(user: UserData | null) => void> = [];

  constructor() {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      const savedUser = localStorage.getItem('authInfo');
      const fallbackProfile = localStorage.getItem('userProfile');
      if (savedUser || fallbackProfile) {
        try {
          this.user = JSON.parse(savedUser || fallbackProfile || "{}");
        } catch (error) {
          this.user = null;
        }
      }
    }
  }

  setUser(userData: UserData) {
    this.user = userData;
    const persistedUserData = toPersistedUserData(userData);

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('authInfo', JSON.stringify(persistedUserData));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userProfile', JSON.stringify({
        firstName: persistedUserData.firstName,
        lastName: persistedUserData.lastName,
        email: persistedUserData.email,
        userId: persistedUserData.userId,
        firebaseUid: persistedUserData.firebaseUid,
        cityId: persistedUserData.cityId,
        state: persistedUserData.state,
        picture: persistedUserData.picture,
      }));
    }

    // Notify listeners
    this.notifyListeners();
  }

  getUser(): UserData | null {
    return this.user;
  }

  clearUser() {
    this.user = null;

    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('authInfo');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userProfile');
      localStorage.removeItem('landingPage');
      localStorage.removeItem('accessToken');
    }

    // Notify listeners
    this.notifyListeners();
  }

  subscribe(listener: (user: UserData | null) => void) {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.user));
  }
}

// Create singleton instance
const userStoreInstance = new UserStore();

// Export actions matching senior's pattern
export const actions = {
  setUser: (userData: UserData) => userStoreInstance.setUser(userData),
  getUser: () => userStoreInstance.getUser(),
  clearUser: () => userStoreInstance.clearUser(),
};

// Export store instance
export default userStoreInstance;
